<div class="[ w-full flex gap-s items-center ][ surface ][ m-bs-0 p-is-m p-ie-s p-b-s ] [ corner-top-s ]" data-cy="table-header">
  <div
    class="[ title-large truncate ][ transition-all duration-short-3 ]"
    [ngClass]="!disableGrowSearchOnFocus && (searchFocus || searchTermControl.value) ? ['w-0'] : ['basis-1/2', 'grow']"
  >
    {{ headline }}
  </div>
  <div class="[ flex gap-2xs basis-1/2 grow items-center ]">
    <ng-content select="[beforeSearch]"></ng-content>

    @if (showSearchTerm) {
      <campus-text-field
        type="search"
        class="[ flex-1 ]"
        [disabled]="showOnlySelection"
        [formControl]="searchTermControl"
        (keyup.enter)="onSearchTermEnterKeyUp($event)"
        (focusout)="onSearchTermFocusOut($event)"
        (focusin)="onSearchTermFocusIn()"
        (clear)="onSearchTermClear()"
        (keydown)="$event.stopPropagation()"
        data-cy="table-search-field"
      ></campus-text-field>
    } @else {
      <div class="[ flex-1 ]"></div>
    }

    <ng-content select="[afterSearch]"></ng-content>

    @if (filterableColumns.length) {
      <button
        inline-icon-button
        icon="cds-comp:filter"
        [class.selected]="showFilters"
        [badge]="filtersForm?.controls.length"
        [disabled]="showOnlySelection"
        (click)="showFilters = !showFilters"
        [plainTooltip]="showFilters ? 'Filters verbergen' : 'Filters tonen'"
        data-cy="btn-table-toggle-filters"
      ></button>
    }

    <ng-content select="[afterFilters]"></ng-content>

    @if (selectableColumns.length) {
      <button inline-icon-button icon="cds-comp:tune" #tune plainTooltip="Kolommen"></button>
      <campus-menu [anchor]="tune" positioning="popover" [multiple]="true" (selectionChange)="onVisibleColumnsChange($event)">
        @for (column of selectableColumns; track column) {
          <campus-menu-item
            checkbox
            [label]="column.label"
            [value]="column"
            [disabled]="column.alwaysVisible"
            [selected]="column.visible || column.alwaysVisible"
          ></campus-menu-item>
        }
      </campus-menu>
    }

    <ng-content select="[afterColumns]"></ng-content>

    @if (showRefresh) {
      <button
        #refresh
        inline-icon-button
        icon="cds-comp:refresh"
        plainTooltip="Opnieuw zoeken"
        (click)="onRefresh($event)"
        data-cy="btn-table-refresh"
      ></button>
    }

    @if (selectable) {
      <button
        inline-icon-button
        icon="cds-comp:show-selection"
        [disabled]="!selectableController.selected.length && !showOnlySelection"
        [badge]="selectableController.selected.length"
        [class.selected]="showOnlySelection"
        (click)="onToggleShowOnlySelection()"
        [plainTooltip]="showOnlySelection ? 'Alle resultaten tonen' : 'Alleen geselecteerde tonen'"
        data-cy="btn-table-toggle-show-selection"
      ></button>
    }

    @if (selectableController.selected?.length && getBulkActions()?.length > 0) {
      <div class="[ relative ]">
        <button inline-icon-button icon="cds-comp:more-vert" #moreBulkActions data-cy="btn-table-bulk-actions"></button>
        <campus-menu [anchor]="moreBulkActions" positioning="popover">
          @for (action of getBulkActions(); track action) {
            <campus-menu-item [label]="action.label" [leadingIcon]="action.icon" (click)="onBulkClick(action)"></campus-menu-item>
          }
        </campus-menu>
      </div>
    }

    <ng-content select="[afterBulkActions]"></ng-content>
  </div>
</div>

@if (showFilters && !showOnlySelection) {
  <div class="[ overflow-auto surface-container stack p-b-s p-i-m ]">
    @for (filter of filtersForm.controls; track filter; let i = $index) {
      <div class="[ flex gap-2xs items-center ]" [formGroup]="filter">
        <dcr-select
          (change)="filterSelect(filter, i)"
          formControlName="key"
          closeOnSelect
          menuPositioning="popover"
          data-cy="select-table-filter-column"
        >
          @for (column of filterableColumns; track column) {
            <dcr-select-option [value]="column.key" [label]="column.label" data-cy="option-table-filter-column"></dcr-select-option>
          }
        </dcr-select>
        <span class="label-large">=</span>
        @if (columnConfigDict[filter.get('key')?.value]; as columnConfig) {
          @if (columnConfig?.filterOptions?.length) {
            @if (!columnConfig.useSingleSelect) {
              <campus-select
                formControlName="value"
                menuPositioning="popover"
                class="[ flex-1 ]"
                multiple
                checkbox
                clearable
                [compareWith]="filterOptionCompareWith"
                data-cy="select-table-filter-value"
              >
                @for (option of columnConfig.filterOptions; track option) {
                  <campus-select-option [value]="option" [label]="option.label" data-cy="option-table-filter-value"></campus-select-option>
                }
              </campus-select>
            } @else {
              <campus-select
                formControlName="value"
                menuPositioning="popover"
                class="[ flex-1 ]"
                clearable
                [compareWith]="filterOptionCompareWith"
                data-cy="select-table-filter-value"
              >
                @for (option of columnConfig.filterOptions; track option) {
                  <campus-select-option
                    [value]="[option.id]"
                    [label]="option.label"
                    data-cy="option-table-filter-value"
                  ></campus-select-option>
                }
              </campus-select>
            }
          } @else {
            @switch (columnConfig?.type) {
              @case ('boolean') {
                <campus-switch formControlName="value" data-cy="toggle-table-filter-value"></campus-switch>
              }
              @case ('number') {
                <dcr-text-field
                  type="number"
                  class="[ flex-1 ]"
                  formControlName="value"
                  data-cy="input-table-filter-value"
                  (keyup.enter)="$event.target.blur()"
                ></dcr-text-field>
              }
              @default {
                <dcr-text-field
                  (keyup.enter)="$event.target.blur()"
                  class="[ flex-1 ]"
                  formControlName="value"
                  data-cy="input-table-filter-value"
                ></dcr-text-field>
              }
            }
          }
        }
        <button
          inline-icon-button
          icon="cds-comp:remove-circle"
          (click)="onRemoveFilter(i)"
          class="[ m-is-auto ]"
          data-cy="btn-remove-filter"
        ></button>
      </div>
    }
    <div class="[ flex justify-between items-center ]">
      <button outline-button leadingIcon="cds-comp:add" (click)="onAddFilter()" data-cy="btn-add-filter">Filter toevoegen</button>
      <button
        inline-button
        leadingIcon="cds-comp:clear"
        (click)="onRemoveAllFilters()"
        [disabled]="!filtersForm.controls.length"
        data-cy="btn-remove-all-filters"
      >
        Filters wissen
      </button>
    </div>
  </div>
}

@if (scrolled$ | async; as scrolled) {
  <div class="[ flex-1 ][ w-full ][ overflow-auto border-bs ]" #scrollContainer data-cy="table-content">
    <table class="[ w-full ]">
      <thead
        #headRef
        class="[ sticky top-0 ][ text-left label-medium on-surface-variant ][ index-content-on-sticky ][ transition-elevation ]"
        [class.elevation-2]="scrolled?.y"
        data-cy="table-thead"
      >
        <tr class="[ row-h-m ][]" data-cy="table-thead-row">
          @if (selectable) {
            <th
              class="[ sticky left-0 index-content-on-sticky ][ surface-container-low on-surface-variant ][ p-i-s p-b-xs ][ text-center ][ column-w-m ]"
              data-cy="table-cell"
            >
              @if (multiple && showCheckAll) {
                <campus-checkbox
                  [checked]="allVisibleRowsChecked"
                  (click)="onCheckAll()"
                  [indeterminate]="!allVisibleRowsChecked && someVisibleRowsChecked"
                  aria-hidden="true"
                  [disabled]="loading"
                  data-cy="checkbox-table-thead-check-all"
                ></campus-checkbox>
              }
              <span
                class="[ absolute inset-0 elevation-0 index-pullback ][ transition-elevation ]"
                [class.elevation-1]="scrolled?.x"
              ></span>
            </th>
          }
          @for (column of visibleColumns.selected; track column) {
            <th
              class="[ surface-container-low on-surface-variant ][ p-0 ][ outline-none ]"
              [attr.tabindex]="column.sortable ? 0 : undefined"
              #sortableHeader
              [attr.aria-sort]="sortBy.direction === 'asc' ? 'ascending' : sortBy.direction === 'desc' ? 'descending' : undefined"
              [style.width]="column.width"
              [style.minWidth]="column.minWidth"
              [style.maxWidth]="column.maxWidth"
              data-cy="table-cell"
            >
              <div
                class="[ relative ][ flex flex-start items-center gap-2xs ][ p-i-s p-b-xs corner-full ]"
                [class.cursor-pointer]="column.sortable"
                (click)="column.sortable && onSortToggle(column)"
              >
                <div>{{ column.label }}</div>
                @if (column.sortable) {
                  <campus-icon
                    [svgIcon]="sortBy.direction ? 'cds-comp:' + sortBy.direction : null"
                    class="[ icon-size-xs ]"
                    [ngClass]="[sortBy.direction && sortBy.key === column.key ? 'opacity-full' : 'opacity-none']"
                  ></campus-icon>
                }
                @if (column.sortable) {
                  <campus-state-layer [for]="sortableHeader" class="[ bg-primary ]"></campus-state-layer>
                  <campus-focus-ring [for]="sortableHeader" inward></campus-focus-ring>
                }
              </div>
            </th>
          }
          @if (inlineActionsTemplate) {
            <th class="[ surface-container-low on-surface-variant ][ column-w-m p-is-s p-ie-2xs p-b-xs ]" data-cy="table-actions-cell"></th>
          }
          @if (hasRowActions) {
            <th class="[ surface-container-low on-surface-variant ][ column-w-m p-is-s p-ie-2xs p-b-xs ]" data-cy="table-cell"></th>
          }
        </tr>
      </thead>
      <tbody class="[ surface ]" data-cy="table-tbody">
        @if (dataSource?.length) {
          @if (groupBy) {
            @for (group of dataSource; track group) {
              <tr
                #groupRowRef
                class="[ sticky ][ border-bs ][ surface-container-lowest ][ index-content-on-sticky ]"
                [style.top.px]="headRef.clientHeight - 2"
                data-cy="table-tbody-group-row"
              >
                <td
                  class="[ relative sticky left-0 ][ p-i-s p-b-xs ][ body-medium ][ outline-none ]"
                  [attr.colspan]="visibleColumns.selected.length + (selectable ? 1 : 0) + (hasRowActions ? 1 : 0)"
                  data-cy="table-cell"
                >
                  @if (loading) {
                    <div class="table-skeleton"></div>
                  } @else {
                    <div class="[ flex justify-between items-center w-full sticky ]">
                      <div class="[ flex items-center gap-s ]">
                        <div class="[ transform transition-transform ]" [class.-rotate-90]="!group.open">
                          <button inline-icon-button icon="cds-comp:expand-more" (click)="onToggleCollapseGroup(group)"></button>
                        </div>
                        <div class="[ flex gap-2xs items-baseline ]">
                          <span class="[ body-small on-surface-variant ]">{{ columnConfigDict[groupBy].label }}:</span>
                          <span class="[ title-medium ]">{{ group[groupBy] }}</span>
                        </div>
                      </div>
                      @if (hasGroupActions) {
                        <div class="[ relative ][ p-is-s p-ie-2xs p-b-xs ]">
                          @if (getGroupActions(group).length > 0) {
                            <div class="[ relative ]">
                              <button #groupActionsRef inline-icon-button icon="cds-comp:more-vert"></button>
                              <campus-menu [anchor]="groupActionsRef" positioning="popover" stopClickPropagationWhenOpen>
                                @for (action of getGroupActions(group); track action) {
                                  <campus-menu-item
                                    [label]="action.label"
                                    [leadingIcon]="action.icon"
                                    (click)="onGroupActionClick(action, group)"
                                  ></campus-menu-item>
                                }
                              </campus-menu>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </td>
              </tr>
              <ng-container *ngTemplateOutlet="dataRow; context: { $implicit: group.children, collapsed: !group.open }"></ng-container>
            }
          } @else {
            <ng-container *ngTemplateOutlet="dataRow; context: { $implicit: dataSource }"></ng-container>
          }
        } @else {
          @if (loading) {
            @for (__ of [0, 1, 2]; track __) {
              <tr class="[ border-bs ]" data-cy="table-tbody-row-skeleton">
                @if (selectable) {
                  <td class="[ p-i-s p-b-xs ][ body-medium ][ outline-none ]">
                    <div class="table-skeleton"></div>
                  </td>
                }
                @for (_ of visibleColumns.selected || [0, 1, 2, 3, 4]; track _) {
                  <td class="[ p-i-s p-b-xs ][ body-medium ][ outline-none ]">
                    <div class="table-skeleton"></div>
                  </td>
                }
                @if (hasRowActions) {
                  <td class="[ p-i-s p-b-xs ][ body-medium ][ outline-none ]">
                    <div class="table-skeleton"></div>
                  </td>
                }
              </tr>
            }
          } @else {
            <tr class="[ border-bs ]" data-cy="table-tbody-row-no-data">
              <td
                class="[ p-i-s p-b-xs ][ body-medium ][ outline-none ]"
                [attr.colspan]="visibleColumns.selected.length + (selectable ? 1 : 0) + (hasRowActions ? 1 : 0)"
              >
                {{ data === null ? noDataMessage : noResultsMessage }}
              </td>
            </tr>
          }
        }
        <ng-template #dataRow let-rows let-collapsed="collapsed">
          @for (row of rows; track trackById(i, row); let i = $index) {
            <tr
              table-row
              #rowRef
              class="[ border-bs ][ row-h-s ]"
              [selectable]="selectable"
              [value]="row"
              [class.collapse]="collapsed"
              [elevateCheckbox]="scrolled?.x"
              [loading]="loading"
              data-cy="table-tbody-row"
              [state]="row.state"
            >
              @for (column of visibleColumns.selected; track column) {
                <td
                  #cellRef
                  class="[ relative ][ p-i-s p-b-xs ][ body-medium ][ outline-none ]"
                  [class.cursor-pointer]="!readonly"
                  [tabindex]="column.type === 'link' ? -1 : 0"
                  (click)="onRowClick($event, row)"
                  data-cy="table-cell"
                >
                  @if (loading) {
                    <div class="table-skeleton"></div>
                  } @else {
                    <ng-container *ngTemplateOutlet="cellValue; context: { $implicit: row, column: column }"></ng-container>
                    @if (!readonly) {
                      <campus-state-layer [for]="rowRef.element" class="[ bg-primary ]"></campus-state-layer>
                    }
                    <campus-focus-ring [for]="cellRef" inward></campus-focus-ring>
                  }
                </td>
              }
              @if (inlineActionsTemplate) {
                <td>
                  <ng-container *ngTemplateOutlet="inlineActionsTemplate; context: { $implicit: row, person: row }"></ng-container>
                </td>
              }
              @if (hasRowActions) {
                <td class="[ relative ][ p-is-s p-ie-2xs p-b-xs ]" data-cy="table-cell-action">
                  @if (loading) {
                    <div class="cds-table-v2-skeleton"></div>
                  } @else {
                    @if (getRowActions(row).length > 0) {
                      <div class="[ relative ]">
                        <button #rowActionsRef inline-icon-button icon="cds-comp:more-vert" data-cy="btn-open-action-menu"></button>
                        <campus-menu
                          [anchor]="rowActionsRef"
                          positioning="popover"
                          stopClickPropagationWhenOpen
                          data-cy="table-cell-action-menu"
                        >
                          @for (action of getRowActions(row); track action) {
                            <campus-menu-item
                              [label]="action.label"
                              [leadingIcon]="action.icon"
                              (click)="onRowActionClick(action, row)"
                              [disabled]="action.disabled"
                              data-cy="table-cell-action-menu-item"
                            ></campus-menu-item>
                          }
                        </campus-menu>
                      </div>
                    }
                    @if (!readonly) {
                      <campus-state-layer [for]="rowRef.element" class="[ bg-primary ]"></campus-state-layer>
                    }
                  }
                </td>
              }
            </tr>
          }
        </ng-template>
      </tbody>
    </table>
    <div class="[ fab-spacer ]"></div>
  </div>
}

@if (pageSize && pagination) {
  <div
    class="[ pagination w-full flex gap-xs items-center justify-end ][ surface-container-low ][ m-be-0 p-is-m p-ie-s p-b-2xs ] [ border-bs corner-bottom-s ]"
    data-cy="table-pagination"
  >
    <div class="[ label-medium on-surface-variant ]">
      <span #pageRef class="[ relative ][ cursor-pointer ][ p-i-xs p-b-2xs corner-full ]">
        {{ pagination.from }} - {{ pagination.to }} van {{ pagination.total }}
        <campus-menu [anchor]="pageRef" positioning="popover" (selectionChange)="onPageSizeChange($event)">
          @for (size of pageSizeOptions; track size) {
            <campus-menu-item [label]="size + ' per pagina'" [value]="size" [selected]="size === pageSize"></campus-menu-item>
          }
        </campus-menu>
        <campus-state-layer [for]="pageRef" class="[ bg-primary ]"></campus-state-layer>
        <campus-focus-ring [for]="pageRef"></campus-focus-ring>
      </span>
    </div>
    <div class="[ flex ]">
      <button
        inline-icon-button
        icon="cds-comp:page-previous"
        class="[ surface-container-low ]"
        (click)="previousPage()"
        [disabled]="loading || page === 1"
        [plainTooltip]="page > 1 && 'Vorige pagina'"
        data-cy="btn-table-pagination-previous"
      ></button>
      <button
        inline-icon-button
        icon="cds-comp:page-next"
        class="[ surface-container-low ]"
        (click)="nextPage()"
        [disabled]="loading || page * pageSize >= totalItems"
        [plainTooltip]="page * pageSize < totalItems && 'Volgende pagina'"
        data-cy="btn-table-pagination-next"
      ></button>
    </div>
  </div>
}

<ng-content select="[fab-button],campus-speed-dial"></ng-content>

<ng-template #cellValue let-row let-column="column">
  @switch (column.type) {
    @case ('number') {
      <div class="w-full">
        {{ row[column.key] }}
      </div>
    }
    @case ('date') {
      <span class="body-small on-surface-variant">
        {{ row[column.key] | date: 'dd/MM/yyyy' }}
      </span>
    }
    @case ('datetime') {
      <span class="body-small on-surface-variant">
        {{ row[column.key] | date: 'dd/MM/yyyy HH:mm:ss' }}
      </span>
    }
    @case ('currency') {
      <span class="label-medium">
        {{ row[column.key] | currency: 'EUR' : 'symbol' : '1.2-2' : locale }}
      </span>
    }
    @case ('chips') {
      <div>
        @for (chip of row[column.key]; track chip) {
          <campus-input-chip
            [label]="chip.label"
            (click)="chip.handler()"
            clickPreventDefault
            clickStopPropagation
            [removable]="true"
          ></campus-input-chip>
        }
      </div>
    }
    @case ('count') {
      <div>{{ row[column.key]?.length || 0 }}</div>
    }
    @case ('capped-chips') {
      <div class="reel">
        <campus-capped-chip [chips]="row[column.key]"></campus-capped-chip>
      </div>
    }
    @case ('link') {
      <a [href]="row.href" #linkRef class="[ relative outline-none ]" [attr.target]="column.target || undefined">
        {{ row[column.key] }}
        <campus-focus-ring [for]="linkRef"></campus-focus-ring>
      </a>
    }
    @case ('routerLink') {
      <a [routerLink]="row.routerLink" #linkRef class="[ relative outline-none ]" [attr.target]="column.target || undefined">
        {{ row[column.key] }}
        <campus-focus-ring [for]="linkRef"></campus-focus-ring>
      </a>
    }
    @case ('links') {
      @if (row[column.key]?.length) {
        <dcr-inline-icon-button
          campusClickStopPropagation
          [id]="column.key + row.id"
          [icon]="'components:' + column.key"
          [badge]="row[column.key].length"
        ></dcr-inline-icon-button>
        <dcr-menu [anchor]="column.key + row.id" positioning="popover">
          @for (link of row[column.key]; track link) {
            <dcr-menu-item campusClickStopPropagation type="link" [href]="link.link" [label]="link.label" target="_blank"></dcr-menu-item>
          }
        </dcr-menu>
      }
    }
    @case ('boolean') {
      <div class="flex place-content-center">
        <campus-icon
          [svgIcon]="row[column.key] ? 'cds-comp:boolean-true' : 'cds-comp:boolean-false'"
          [attr.data-cy]="row[column.key] ? 'table-cell-boolean-true' : 'table-cell-boolean-false'"
          [class.icon-opacity-disabled]="!row[column.key]"
        ></campus-icon>
      </div>
    }
    @case ('boolean-sync') {
      <div class="flex place-content-center">
        @if (row[column.key]) {
          <campus-icon svgIcon="sync" [class.rotate]="!!row[column.key]"></campus-icon>
        }
      </div>
    }
    @case ('icon') {
      <ng-container class="body-medium">
        <campus-icon [svgIcon]="row[column.key]"></campus-icon>
      </ng-container>
    }
    <!-- TODO: #25611 replace with dcr-badge component-->
    @case ('status') {
      <div class="flex place-content-center">
        <div
          class="[ block icon-size-xs corner-full ] [ border ]"
          [ngClass]="['stage-published', 'border-stage-stroke-published']"
          [class.bg-success]="row[column.key] === 'PUBLISHED'"
          [class.bg-warn-container]="row[column.key] === 'DRAFT'"
        ></div>
      </div>
    }
    @default {
      <div
        class="body-medium"
        [ngClass]="column.clamp ? 'line-clamp-' + column.clamp : undefined"
        [richTooltip]="!disableTooltipOnTruncated && row[column.key]"
        [tooltipSubhead]="column.label"
        [tooltipYOffset]="10"
        tooltipPosition="absolute"
        tooltipWhenTruncated
        [tooltipDisabled]="disableTooltip"
      >
        {{ row[column.key] }}
      </div>
    }
  }
</ng-template>
